import type { Observation } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import { EM_DASH } from '../../core/copy';
import type { FhirView } from '../../core/view-contract';
import { getChoiceValue } from '../../resolvers/choice';

import {
  formatChoiceValue,
  interpretationFlag,
  observationDateText,
  referenceRangeText,
  worstComponentFlag,
} from './format';
import {
  composedObservationValue,
  panelComponents,
  resolveObservationPresentation,
} from './presentations';

/**
 * The exported Observation component. Dispatches internally on the render
 * context — surface- or placement-specific variants stay private to this
 * directory.
 */
export const ObservationView: FhirView<Observation> = (props) => {
  if (props.ctx.surface === 'print' || props.ctx.surface === 'pdf') {
    return <ObservationPrintView {...props} />;
  }
  if (props.ctx.inList) return <ObservationCompactView {...props} />;
  if (props.ctx.placement === 'side-panel') {
    return <ObservationPanelView {...props} />;
  }
  return <ObservationReadView {...props} />;
};

// Presentation table (LOINC/profile-keyed, e.g. BP panel) feeds these two
// helpers; every variant below gets vital-specific display with zero
// vital-specific code. See presentations.ts.
function observationValue(data: Observation): string {
  const presentation = resolveObservationPresentation(data);
  if (presentation) {
    const composed = composedObservationValue(data, presentation);
    if (composed) return composed;
  }
  const choice = getChoiceValue(data, 'value');
  return choice && choice.type !== 'absent'
    ? formatChoiceValue(choice)
    : EM_DASH;
}

function observationName(data: Observation): string {
  return (
    resolveObservationPresentation(data)?.title ??
    data.code?.text ??
    data.code?.coding?.[0]?.display ??
    'Observation'
  );
}

function InterpretationTag({ data }: { data: Observation }) {
  // Panels rarely carry a root interpretation — inherit the worst component's.
  const flag = interpretationFlag(data) ?? worstComponentFlag(data);
  if (!flag) return null;
  return <Tag pill subtle size="xs" color={flag.tone} label={flag.label} />;
}

/**
 * Per-component rows for panel presentations (e.g. BP: Systolic / Diastolic),
 * each with its own interpretation flag and reference range — the composed
 * headline says "158/96 mmHg", this says which half is the problem. Generic
 * over any presentation with components; renders nothing otherwise.
 */
function PanelBreakdown({
  data,
  showRanges = true,
}: {
  data: Observation;
  showRanges?: boolean;
}) {
  const presentation = resolveObservationPresentation(data);
  const entries = presentation ? panelComponents(data, presentation) : [];
  if (entries.length === 0) return null;
  return (
    <div className="mt-2 space-y-1 border-t border-border-primary pt-2">
      {entries.map(({ label, component }) => {
        const flag = interpretationFlag(component);
        const range = showRanges ? referenceRangeText(component) : undefined;
        const quantity = component.valueQuantity;
        const valueText =
          quantity?.value !== undefined
            ? `${quantity.value}${quantity.unit ? ` ${quantity.unit}` : ''}`
            : EM_DASH;
        return (
          <Grid
            key={label}
            align="baseline"
            className="grid-cols-[minmax(0,1fr)_auto] gap-x-4"
          >
            <Stack
              direction="horizontal"
              align="baseline"
              className="min-w-0 gap-x-3"
            >
              <Text inline size="xs" color="tertiary" className="w-16 shrink-0">
                {label}
              </Text>
              {range && (
                <Text inline size="xs" color="tertiary" className="truncate">
                  {range}
                </Text>
              )}
            </Stack>
            <Stack direction="horizontal" gap={2} align="baseline">
              <Text inline size="sm" weight="semibold" className="tabular-nums">
                {valueText}
              </Text>
              {flag && (
                <Tag
                  pill
                  subtle
                  size="xs"
                  color={flag.tone}
                  label={flag.label}
                />
              )}
            </Stack>
          </Grid>
        );
      })}
    </div>
  );
}

/** Standalone card: the observation is the page's subject. */
const ObservationReadView: FhirView<Observation> = ({ data, ctx }) => {
  const range = referenceRangeText(data);
  const date = observationDateText(data);
  return (
    <div
      data-view="observation-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold">
          {observationName(data)}
        </Text>
        <FhirStatusTag status={data.status} />
      </Stack>
      <Stack direction="horizontal" gap={2} align="baseline" className="mt-1">
        <Text inline size="md" weight="bold" className="tabular-nums">
          {observationValue(data)}
        </Text>
        <InterpretationTag data={data} />
      </Stack>
      <Stack
        direction="horizontal"
        wrap
        align="stretch"
        className="mt-1 gap-x-4"
      >
        {range && (
          <Text inline size="xs" color="tertiary">
            Reference range: {range}
          </Text>
        )}
        {date && (
          <Text inline size="xs" color="tertiary">
            {date}
          </Text>
        )}
      </Stack>
      <PanelBreakdown data={data} />
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};

/** Condensed stacked card for narrow chrome (side panel): value-first, no date. */
const ObservationPanelView: FhirView<Observation> = ({ data }) => {
  const range = referenceRangeText(data);
  return (
    <div
      data-view="observation-panel"
      className="rounded-md border border-border-primary bg-bg-primary px-3 py-2"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="xs" color="tertiary" className="truncate">
          {observationName(data)}
        </Text>
        <InterpretationTag data={data} />
      </Stack>
      <Text inline size="sm" weight="semibold" className="tabular-nums">
        {observationValue(data)}
      </Text>
      {range && (
        <Text size="xs" color="tertiary" className="truncate">
          Reference range: {range}
        </Text>
      )}
      <PanelBreakdown data={data} showRanges={false} />
    </div>
  );
};

/** Lab-style result row for lists (e.g. inside a DiagnosticReport). */
const ObservationCompactView: FhirView<Observation> = ({ data }) => {
  const range = referenceRangeText(data);
  return (
    <Grid
      data-view="observation-compact"
      align="center"
      className="grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-2"
    >
      <div className="min-w-0">
        <Text size="sm" weight="regular" className="truncate">
          {observationName(data)}
        </Text>
        {range && (
          <Text size="xs" color="tertiary" className="truncate">
            Reference range: {range}
          </Text>
        )}
      </div>
      <Stack direction="horizontal" gap={2} align="center">
        <Text inline size="sm" weight="semibold" className="tabular-nums">
          {observationValue(data)}
        </Text>
        <InterpretationTag data={data} />
      </Stack>
    </Grid>
  );
};

const ObservationPrintView: FhirView<Observation> = ({ data }) => (
  <Grid
    data-view="observation-print"
    className="grid-cols-[1fr_auto_auto] gap-x-4"
  >
    <span>{observationName(data)}</span>
    <span>{observationValue(data)}</span>
    <span>{referenceRangeText(data) ?? EM_DASH}</span>
  </Grid>
);
